/**
 * useSelectOptions Hook
 * 
 * Simplifies the process of fetching and transforming API data into select options.
 * Combines API query, data transformation, and memoization into a single hook.
 * 
 * @example
 * ```typescript
 * // Simple usage
 * const { options, isLoading } = useSelectOptions({
 *   endpoint: 'getDeviceManufacturers',
 *   dataKey: 'manufacturers',
 *   valueField: 'manufacturerNumber',
 *   labelField: 'manufacturerName',
 * })
 * 
 * // With custom transformer
 * const { options, isLoading } = useSelectOptions({
 *   endpoint: 'getDeviceTypes',
 *   dataKey: 'deviceTypes',
 *   transformer: (data) => filterDeviceTypesByManufacturer(data, selectedManufacturer),
 * })
 * ```
 */

import * as React from 'react'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  transformMapForListBox,
  type SelectOption
} from '@/lib/dataTransformationUtils'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Type Definitions
// ============================================================================

export interface UseSelectOptionsConfig {
  /** API endpoint name to fetch data from */
  endpoint: string
  /** Key in the API response that contains the array of items */
  dataKey: string
  /** Field name to use as the option value */
  valueField?: string
  /** Field name to use as the option label */
  labelField?: string
  /** Custom transformer function (overrides valueField/labelField if provided) */
  transformer?: (data: any[]) => SelectOption[]
  /** Query parameters for the API call */
  queryParams?: Record<string, any>
  /** Query options (staleTime, etc.) */
  queryOptions?: Record<string, any>
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSelectOptions(config: UseSelectOptionsConfig) {
  const { 
    endpoint, 
    dataKey, 
    valueField, 
    labelField, 
    transformer,
    queryParams = {},
    queryOptions = {}
  } = config

  log.debug(`Initializing for endpoint "${endpoint}"`, { dataKey, valueField, labelField })

  // Fetch data from API
  const { data, isLoading, error } = useApiQuery(
    endpoint,
    queryParams,
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnWindowFocus: false,
      ...queryOptions,
    }
  )

  React.useEffect(() => {
    if (error) {
      log.error(`Error loading data for endpoint "${endpoint}":`, error)
    }
  }, [error, endpoint])

  // Transform data into select options
  const options = React.useMemo(() => {
    if (!data) {
      log.debug(`No data for endpoint "${endpoint}"`)
      return []
    }

    // Extract the array from the response — unwrap content wrapper if present
    const responseData = data as Record<string, any>
    const unwrapped = (responseData?.content != null ? responseData.content : responseData) as Record<string, any>
    const items = unwrapped[dataKey]

    if (!Array.isArray(items)) {
      log.warn(`dataKey "${dataKey}" is not an array for endpoint "${endpoint}"`, { responseData })
      return []
    }

    if (items.length === 0) {
      log.debug(`Empty array for endpoint "${endpoint}"`)
      return []
    }

    // Use custom transformer if provided
    if (transformer) {
      const transformed = transformer(items)
      log.debug(`Transformed ${items.length} items to ${transformed.length} options for endpoint "${endpoint}"`)
      return transformed
    }

    // Use standard transformation if valueField and labelField are provided
    if (valueField && labelField) {
      const transformed = transformMapForListBox(items, {
        valueField,
        labelField,
      })
      log.debug(`Transformed ${items.length} items to ${transformed.length} options for endpoint "${endpoint}" using valueField="${valueField}", labelField="${labelField}"`)
      return transformed
    }

    // Return empty array if no transformation method is available
    log.warn(
      `No transformation method provided for endpoint "${endpoint}". ` +
      `Either provide valueField/labelField or a custom transformer.`
    )
    return []
  }, [data, dataKey, valueField, labelField, transformer, endpoint])

  return {
    options,
    isLoading,
    error,
    data, // Expose raw data in case it's needed
  }
}

// ============================================================================
// Hook for Multiple Select Options
// ============================================================================

/**
 * useSelectOptionsMap Hook
 * 
 * Fetches and transforms multiple select options at once.
 * Returns a map of field names to options and loading states.
 * Supports dependent/cascading fields with dynamic transformers.
 * 
 * @example
 * ```typescript
 * // Simple usage - multiple independent selects
 * const { optionsMap, loadingMap } = useSelectOptionsMap({
 *   manufacturerNumber: {
 *     endpoint: 'getDeviceManufacturers',
 *     dataKey: 'manufacturers',
 *     valueField: 'manufacturerNumber',
 *     labelField: 'manufacturerName',
 *   },
 *   distributorNumber: {
 *     endpoint: 'getDeviceDistributors',
 *     dataKey: 'distributors',
 *     valueField: 'distributorNumber',
 *     labelField: 'distributorName',
 *   },
 * })
 * 
 * // With cascading/dependent field
 * const { optionsMap, loadingMap, rawData } = useSelectOptionsMap({
 *   manufacturerNumber: {
 *     endpoint: 'getDeviceManufacturers',
 *     dataKey: 'manufacturers',
 *     valueField: 'manufacturerNumber',
 *     labelField: 'manufacturerName',
 *   },
 *   deviceTypeNumber: {
 *     endpoint: 'getDeviceTypes',
 *     dataKey: 'deviceTypes',
 *     // Use dependentTransformer to filter based on another field
 *     dependentTransformer: (data, dependencies) => 
 *       filterDeviceTypesByManufacturer(data, dependencies?.selectedManufacturer),
 *   },
 * }, { selectedManufacturer })
 * ```
 */

export interface SelectOptionsMapConfig {
  [fieldName: string]: Omit<UseSelectOptionsConfig, 'endpoint'> & {
    endpoint: string
    /** Transformer that depends on external state (e.g., another field's value) */
    dependentTransformer?: (data: any[], dependencies?: Record<string, any>) => SelectOption[]
  }
}

export interface UseSelectOptionsMapReturn {
  /** Map of field names to their transformed options */
  optionsMap: Record<string, SelectOption[]>
  /** Map of field names to their loading states */
  loadingMap: Record<string, boolean>
  /** Raw API response data for each field (useful for cascading logic) */
  rawData: Record<string, any>
  /** Individual results with all details */
  results: Record<string, ReturnType<typeof useSelectOptions>>
}

export function useSelectOptionsMap(
  config: SelectOptionsMapConfig,
  dependencies?: Record<string, any>
): UseSelectOptionsMapReturn {
  // Ensure stable key order by sorting keys
  const fieldNames = React.useMemo(() => Object.keys(config).sort(), [config])
  
  // Fetch all options in parallel
  // NOTE: This calls hooks in a loop, which is generally not recommended.
  // However, it's safe here because:
  // 1. The config object is stable (same keys every render)
  // 2. We sort the keys to ensure consistent order
  // 3. The number of hooks called is constant for each component instance
  const results: Record<string, ReturnType<typeof useSelectOptions>> = {}
  
  // Call hooks for each field in a stable, sorted order
  for (const fieldName of fieldNames) {
    const fieldConfig = config[fieldName]
    // eslint-disable-next-line react-hooks/rules-of-hooks
    results[fieldName] = useSelectOptions({
      endpoint: fieldConfig.endpoint,
      dataKey: fieldConfig.dataKey,
      valueField: fieldConfig.valueField,
      labelField: fieldConfig.labelField,
      transformer: fieldConfig.transformer,
      queryParams: fieldConfig.queryParams,
      queryOptions: fieldConfig.queryOptions,
    })
  }

  // Build options map (with dependent transformers)
  const optionsMap = React.useMemo(() => {
    const map: Record<string, SelectOption[]> = {}
    
    for (const fieldName of fieldNames) {
      const fieldConfig = config[fieldName]
      const result = results[fieldName]
      
      // If field has a dependent transformer, use it with dependencies
      if (fieldConfig.dependentTransformer && result?.data) {
        const responseData = result.data as Record<string, any>
        const unwrapped = (responseData?.content != null ? responseData.content : responseData) as Record<string, any>
        const items = unwrapped[fieldConfig.dataKey]
        if (Array.isArray(items)) {
          map[fieldName] = fieldConfig.dependentTransformer(items, dependencies)
          continue
        }
      }
      
      // Otherwise use pre-transformed options
      map[fieldName] = result?.options || []
    }
    
    log.debug('Built optionsMap:',
      Object.entries(map).map(([key, opts]) => ({ field: key, optionCount: opts.length }))
    )
    
    return map
  }, [
    fieldNames,
    // Depend on each result's options and data individually to catch updates
    ...fieldNames.map(fn => results[fn]?.options),
    ...fieldNames.map(fn => results[fn]?.data),
    dependencies,
  ])

  // Build loading map
  const loadingMap = React.useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const fieldName of fieldNames) {
      map[fieldName] = results[fieldName]?.isLoading || false
    }
    return map
  }, [fieldNames, ...fieldNames.map(fn => results[fn]?.isLoading)])

  // Build raw data map
  const rawData = React.useMemo(() => {
    const map: Record<string, any> = {}
    for (const fieldName of fieldNames) {
      const result = results[fieldName]
      const responseData = result?.data as Record<string, any> | undefined
      if (responseData) {
        const fieldConfig = config[fieldName]
        const unwrapped = (responseData?.content != null ? responseData.content : responseData) as Record<string, any>
        map[fieldName] = unwrapped[fieldConfig.dataKey] || []
      } else {
        map[fieldName] = []
      }
    }
    return map
  }, [fieldNames, ...fieldNames.map(fn => results[fn]?.data)])

  return {
    optionsMap,
    loadingMap,
    rawData,
    results,
  }
}

